// @ts-strict-ignore
import { LoadConnectionTextureProcessor } from './load-connection-texture.processor';
import { ArticleContainer } from '../model3d/article-container';
import { IOperation } from '@shared/undo/operation/i-operation';
import { Connection } from '../model3d/connection';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementUsageVO, MetaElementVO, SimpleTextValueVO, TextureAreaUsageVO, TextureAreaVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { ContainerService } from '../model3d/container.service';
import { ArticleUtils } from '@article/util/article-utils';
import { UndoRemoveImageConnectionsOperation } from '@shared/undo/operation/undo-remove-image-connections-operation';
import { LabelSequenceOperation } from '@shared/undo/operation/label-sequence-operation';
import { LabelingTextureData } from '@project/components/label/labeling-texture-data';
import { UndoConnectionLabelOperation } from '@shared/undo/operation/undo-connection-label-operation';
import { DefinedMetaElementInfoPropertyDef } from '@project/constants/defined-meta-element-info-property-def';
import { SelectionArticleGroupIdentity } from '@project/components/article/model/vo/selection-article-group-identity';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { LabelingUtils } from '../model3d/util/labeling-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ConnectionCreator } from '@article/components/connections/connection-creation/connection-creator';
import { MetaElementUsageUtils } from '@project/util/meta-element-usage-utils';
import { Injectable } from '@angular/core';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ConnectionInfoUtils } from '../model3d/util/connection-info-utils';
import { UpdateLabelingCompressionProcessor } from './update-labeling-compression.processor';
import { ConnectionInfoService } from '../model3d/service/connection-info.service';
import { PropertyUtils } from '@article/util/property-utils';

@Injectable({ providedIn: 'root' })
export class TextureConnectionUtils {
  constructor(
    private _containerService: ContainerService,
    private _labelingUtils: LabelingUtils,
    private _connectionCreator: ConnectionCreator,
    private _projectArticleService: ProjectArticleService,
    private _selectionModel: SelectionModel,
    private _selectionModelService: SelectionModelService,
    private _updateLabelingCompressionProcessor: UpdateLabelingCompressionProcessor,
    private _connectionInfoService: ConnectionInfoService,
  ) {
  }

  public cleanMultipurposeTextureConnections(
    createdArticles: ArticleContainer[],
    loadConnectionTextureProcessor: LoadConnectionTextureProcessor
  ): IOperation {
    if (ArrayUtils.isEmpty(createdArticles)) {
      return null;
    }
    const textureConnectionCubes: Connection[] = [];
    for (const articleContainer of createdArticles) {
      const backwardConnectedConnectionUsageIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(
        articleContainer.articleUsage
      );
      for (const metaElementUsageId of backwardConnectedConnectionUsageIds) {
        const parentContainer: ArticleContainer = this._containerService.findArticleContainerInGlobal(metaElementUsageId.articleUsageId);
        const acceptingMetaElement: MetaElementVO = ArticleUtils.findMetaElementByName(parentContainer.article, metaElementUsageId.metaElementName);
        const linkedMetaElement: MetaElementVO = ConnectionInfoUtils.findLinkedMultipurposeMetaElement(
          parentContainer.article,
          acceptingMetaElement
        );
        if (linkedMetaElement) {
          const linkedTextureCube: Connection = parentContainer.findFirstConnectionByName(linkedMetaElement.name);
          textureConnectionCubes.push(linkedTextureCube);
        }
      }
    }

    if (textureConnectionCubes.length === 0) {
      return null;
    }
    const undoOperations: IOperation[] = this.cleanLabelTextAndCreateUndoOperation(textureConnectionCubes);
    const imageOperation: UndoRemoveImageConnectionsOperation = this.removeImageAndCreateUndoOperation(
      textureConnectionCubes,
      loadConnectionTextureProcessor
    );
    if (imageOperation) {
      undoOperations.push(imageOperation);
    }

    return new LabelSequenceOperation(
      undoOperations,
      null,
      loadConnectionTextureProcessor,
      this._connectionInfoService,
      this._containerService,
      this._labelingUtils,
      this._updateLabelingCompressionProcessor
    );
  }

  private cleanLabelTextAndCreateUndoOperation(textureConnectionCubes: Connection[]): IOperation[] {
    const operations: IOperation[] = [];
    for (const cube of textureConnectionCubes) {
      if (cube.metaElementUsage instanceof TextureAreaUsageVO) {
        const defaultTextValue: TextValueVO = ConnectionInfoUtils.getTextValueByTextureArea(cube.metaElementVO as TextureAreaVO);
        const textureUsage: TextureAreaUsageVO = cube.metaElementUsage as TextureAreaUsageVO;
        if (defaultTextValue && defaultTextValue.equals(textureUsage.textValue)) {
          continue;
        }
        const oldData: LabelingTextureData = new LabelingTextureData(textureUsage);
        textureUsage.textValue = defaultTextValue || new SimpleTextValueVO();
        operations.push(new UndoConnectionLabelOperation(cube, oldData, this._containerService));
      }
    }

    return operations;
  }

  private removeImageAndCreateUndoOperation(
    textureConnectionCubes: Connection[],
    loadConnectionTextureProcessor: LoadConnectionTextureProcessor
  ): UndoRemoveImageConnectionsOperation {
    let result: UndoRemoveImageConnectionsOperation;
    const cubesWithImage: Connection[] = [];
    for (const cube of textureConnectionCubes) {
      const imageHash: string = ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
        cube.articleContainer.articleUsage,
        cube.connectionName,
        DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH
      );
      if (imageHash) {
        cubesWithImage.push(cube);
      }
    }

    if (cubesWithImage.length > 0) {
      result = new UndoRemoveImageConnectionsOperation(cubesWithImage, loadConnectionTextureProcessor, this._containerService, this._labelingUtils);
    }
    loadConnectionTextureProcessor.cleanConnectionImageCache(textureConnectionCubes);
    ConnectionInfoUtils.removeImagesFromConnectionCubes(textureConnectionCubes);
    return result;
  }

  public getTextureConnectionsBySelection(textureConnectionTypes: string[]): Connection[] {
    let connections: Connection[] = [];
    const selectedObjects: any[] = this._selectionModel.selectedObjects;
    if (selectedObjects.length > 0) {
      if (selectedObjects[0] instanceof MetaElementUsageIdentityVO) {
        connections = this.getSelectedConnectionsOfType(textureConnectionTypes);
      } else if (selectedObjects[0] instanceof SelectionArticleGroupIdentity) {
        connections = this.getTextureConnectionsByArticles(this._selectionModelService.getArticleContainers(), textureConnectionTypes);
      }
    }

    return connections;
  }

  private getSelectedConnectionsOfType(connectionTypes: string[]): Connection[] {
    if (!connectionTypes || connectionTypes.length === 0) {
      return [];
    }
    let connections: Connection[] = [];
    if (ConnectionInfoUtils.isAllConnectionsOfType(this._selectionModelService.getConnections(), connectionTypes)) {
      connections = this._selectionModelService.getConnectionsByType(connectionTypes);
    }
    return connections;
  }

  public getTextureConnectionsByArticles(articleContainers: ArticleContainer[], textureConnectionTypes: string[]): Connection[] {
    const textConnections: Connection[] = [];
    if (articleContainers && articleContainers.length > 0) {
      const connections: Connection[] = ConnectionInfoUtils.findConnectionsInArticles(articleContainers);
      for (const connection of connections) {
        if (textureConnectionTypes.indexOf(connection.type) >= 0) {
          const firstConnection: Connection = this._labelingUtils.findFirstMergedTextureCube(connection);
          if (textConnections.indexOf(firstConnection) === -1 && firstConnection.isActive) {
            textConnections.push(firstConnection);
          }
        }
      }
    }
    return textConnections;
  }

  public getSelectedTextConnectionsWithRelative(onlyDefaultLabel: boolean): Connection[] {
    let textureConnectionCubes: Connection[] = [];
    const selectedObjects: any[] = this._selectionModel.selectedObjects;
    if (selectedObjects.length > 0) {
      if (selectedObjects[0] instanceof MetaElementUsageIdentityVO) {
        textureConnectionCubes = this._selectionModelService.getTextConnections();
      } else if (selectedObjects[0] instanceof SelectionArticleGroupIdentity) {
        const selectedArticlesWithRelative: ArticleContainer[] = this.getSelectedArticlesWithRelative();
        textureConnectionCubes = this.getTextureConnectionsByArticles(selectedArticlesWithRelative, Connection.TEXT_TYPES);
      }
    }
    let filteredTextureCubes: Connection[] = [];
    if (onlyDefaultLabel) {
      for (const textureCube of textureConnectionCubes) {
        if (this._labelingUtils.isTextureCubePartOfDefaultLabeling(textureCube)) {
          filteredTextureCubes.push(textureCube);
        }
      }
    } else {
      filteredTextureCubes = textureConnectionCubes;
    }
    return filteredTextureCubes.filter((connection) => {
      return !PropertyUtils.getStringPropertyValue(connection.metaElementVO.behaviorProperties, 'defaultText');
    });
  }

  private getSelectedArticlesWithRelative(): ArticleContainer[] {
    const selectedArticles: ArticleContainer[] = this._selectionModelService.getArticleContainers();
    const selectedArticlesWithRelative: ArticleContainer[] = selectedArticles.slice();
    for (const selectedArticle of selectedArticles) {
      const connectedArticles: ArticleContainer[] = this._projectArticleService.getConnectedArticlesWithRelative(selectedArticle, false);
      ArrayUtils.mergeArrays(selectedArticlesWithRelative, connectedArticles);
      for (const connectedArticle of connectedArticles) {
        const articlesConnectedToAdapter: ArticleContainer[] = this._projectArticleService.getConnectedArticlesWithRelative(
          connectedArticle,
          false
        );
        ArrayUtils.mergeArrays(selectedArticlesWithRelative, articlesConnectedToAdapter);
      }
    }
    return selectedArticlesWithRelative;
  }

  public getSelectedUnusedLabelingConnectionsWithRelative(): Connection[] {
    let unusedLabelingConnections: Connection[] = [];
    if (this._selectionModel.isConnectionSelection()) {
      const selectedConnections: Connection[] = this._selectionModelService.getConnections().reduce((result: Connection[], current: Connection) => {
        return result.concat(this._connectionCreator.getMultipurposeConnectionsBySelection(current));
      }, []);
      unusedLabelingConnections = this.getUnusedLabelingConnections(selectedConnections);
    } else if (this._selectionModel.isArticleSelection()) {
      const selectedArticlesWithRelative: ArticleContainer[] = this.getSelectedArticlesWithRelative();
      const articlesConnections: Connection[] = ConnectionInfoUtils.findConnectionsInArticles(selectedArticlesWithRelative);
      unusedLabelingConnections = this.getUnusedLabelingConnections(articlesConnections);
    }
    return unusedLabelingConnections;
  }

  private getUnusedLabelingConnections(connections: Connection[]): Connection[] {
    if (!connections) {
      return [];
    }
    return connections.filter((connection: Connection) => {
      if (connection.metaElementVO instanceof ConnectionAreaVO) {
        return ConnectionInfoUtils.isFreeConnection(connection, false) && ConnectionInfoUtils.hasLabelingAccessoryCategory(connection.metaElementVO);
      }
      return false;
    });
  }

  // split merge and refresh texture connections in case when previous/next texture of article container does not exists in articleContainers array
  public updateMergeOfTextureConnectionsInContainers(articleContainers: ArticleContainer[]): void {
    if (ArrayUtils.isEmpty(articleContainers)) {
      return;
    }
    const allArticlesGuids: string[] = articleContainers.map((c) => c.articleUsage.id);
    articleContainers.forEach((container) => {
      container.connections.forEach((cn) => {
        const meta: MetaElementUsageVO = cn.metaElementUsage;
        if (meta instanceof TextureAreaUsageVO) {
          let changed: boolean = false;
          if (meta.previousMergedUsageId && allArticlesGuids.indexOf(meta.previousMergedUsageId.articleUsageId) === -1) {
            meta.previousMergedUsageId = null;
            changed = true;
          }
          if (meta.nextMergedUsageId && allArticlesGuids.indexOf(meta.nextMergedUsageId.articleUsageId) === -1) {
            meta.nextMergedUsageId = null;
            changed = true;
          }
          if (changed) {
            cn.resetTexture();
            cn.isActive = !cn.isActive;
          }
        }
      });
      container.refreshTextureConnections(true, false);
    });
  }

  public findTextureConnectionCubeByRelativeAcceptingAreaId(metaElementUsageId: MetaElementUsageIdentityVO): Connection {
    const acceptingCube: Connection = this._connectionInfoService.findConnectionByIdentity(metaElementUsageId);
    if (!acceptingCube) {
      return null;
    }
    const connectedMetaElementIds: MetaElementUsageIdentityVO[] = MetaElementUsageUtils.forwardConnectedMetaElementUsageIdentities(
      acceptingCube.metaElementUsage
    );
    if (ArrayUtils.isEmpty(connectedMetaElementIds)) {
      return null;
    }
    const targetMetaElementId: MetaElementUsageIdentityVO = connectedMetaElementIds[0];
    const connectedArticleContainer: ArticleContainer = this._containerService.findArticleContainerInGlobal(targetMetaElementId.articleUsageId);
    if (ArticleUtils.isLabelingArticle(connectedArticleContainer.article)) {
      const textureCubes: Connection[] = this.getTextureConnectionsByArticles(
        [connectedArticleContainer],
        Connection.TEXT_TYPES
      );
      if (ArticleUtils.isDynamicArticle(connectedArticleContainer.article)) {
        const textureConnectionName: string = targetMetaElementId.metaElementName.replace(
          MetaElementVO.CONNECTION_PREFIX,
          MetaElementVO.TEXTURE_PREFIX
        );
        return textureCubes.find((cube) => cube.connectionName === textureConnectionName);
      } else {
        return textureCubes[0];
      }
    } else {
      return null;
    }
  }
}
